import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { isValidCnpj } from "../../utils/validateCnpj";
import AuthSplitLayout from "../../components/Auth/AuthSplitLayout";
import ModernAuthField from "../../components/Auth/ModernAuthField";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_MIN_LEN = 8;
const PASSWORD_HAS_UPPERCASE = /[A-Z]/;
const PASSWORD_HAS_SPECIAL = /[^A-Za-z0-9]/;

function getFlagEmoji(countryCode) {
	if (!countryCode || countryCode.length !== 2) return "";
	const a = 0x1f1e6 - 65;
	return String.fromCodePoint(
		...countryCode.toUpperCase().split("").map((c) => a + c.charCodeAt(0))
	);
}

const COUNTRY_LIST = [
	{ code: "BR", ddi: "55", name: "Brasil" },
	{ code: "US", ddi: "1", name: "Estados Unidos" },
	{ code: "AR", ddi: "54", name: "Argentina" },
	{ code: "MX", ddi: "52", name: "México" },
	{ code: "PT", ddi: "351", name: "Portugal" },
	{ code: "ES", ddi: "34", name: "Espanha" },
	{ code: "CO", ddi: "57", name: "Colômbia" },
	{ code: "CL", ddi: "56", name: "Chile" },
	{ code: "PE", ddi: "51", name: "Peru" },
	{ code: "UY", ddi: "598", name: "Uruguai" },
	{ code: "PY", ddi: "595", name: "Paraguai" },
	{ code: "EC", ddi: "593", name: "Equador" },
	{ code: "BO", ddi: "591", name: "Bolívia" },
	{ code: "VE", ddi: "58", name: "Venezuela" },
	{ code: "IT", ddi: "39", name: "Itália" },
	{ code: "FR", ddi: "33", name: "França" },
	{ code: "DE", ddi: "49", name: "Alemanha" },
	{ code: "GB", ddi: "44", name: "Reino Unido" },
	{ code: "JP", ddi: "81", name: "Japão" },
	{ code: "IN", ddi: "91", name: "Índia" },
];

function formatPhoneByCountry(countryCode, digits) {
	const d = (digits || "").replace(/\D/g, "");
	if (countryCode === "BR") {
		if (d.length <= 2) return d;
		if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
		return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
	}
	if (countryCode === "US") {
		if (d.length <= 3) return d;
		if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
		return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
	}
	return d.slice(0, 15);
}

function validatePassword(pwd) {
	if (!pwd || pwd.length < PASSWORD_MIN_LEN)
		return { valid: false, key: "signup.form.passwordHint" };
	if (!PASSWORD_HAS_UPPERCASE.test(pwd))
		return { valid: false, key: "signup.form.passwordWeak" };
	if (!PASSWORD_HAS_SPECIAL.test(pwd))
		return { valid: false, key: "signup.form.passwordWeak" };
	return { valid: true };
}

function isValidEmail(email) {
	return typeof email === "string" && EMAIL_REGEX.test(email.trim());
}

function formatCnpj(v) {
	const d = (v || "").replace(/\D/g, "");
	if (d.length <= 2) return d;
	if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
	if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
	if (d.length <= 12)
		return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
	return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
}

function passwordStrength(password) {
	if (!password) return 0;
	let score = 0;
	if (password.length >= 6) score += 1;
	if (password.length >= 10) score += 1;
	if (PASSWORD_HAS_UPPERCASE.test(password) && PASSWORD_HAS_SPECIAL.test(password)) {
		score += 1;
	}
	return Math.min(score, 3);
}

const submitButtonClass =
	"mt-2 w-full rounded-lg bg-zinc-950 py-2.5 text-sm font-medium text-white transition-all duration-150 hover:bg-zinc-800 active:scale-[0.99] disabled:opacity-50";

const selectClass =
	"w-full rounded-lg border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 outline-none transition-all duration-150 focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900";

function Signup() {
	const history = useHistory();
	const [step, setStep] = useState(1);
	const [email, setEmail] = useState("");
	const [document, setDocument] = useState("");
	const [code, setCode] = useState("");
	const [verificationToken, setVerificationToken] = useState("");
	const [companyName, setCompanyName] = useState("");
	const [name, setName] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [phoneCountry, setPhoneCountry] = useState("BR");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [loading, setLoading] = useState(false);
	const [documentError, setDocumentError] = useState("");
	const [emailError, setEmailError] = useState("");
	const [codeError, setCodeError] = useState("");
	const [passwordError, setPasswordError] = useState("");

	const docClean = document.replace(/\D/g, "");
	const strength = passwordStrength(password);

	const handleSendCode = async (e) => {
		e.preventDefault();
		setEmailError("");
		setDocumentError("");
		const emailTrim = email.trim().toLowerCase();
		if (!emailTrim) {
			setEmailError(i18n.t("signup.form.emailInvalid"));
			return;
		}
		if (!isValidEmail(emailTrim)) {
			setEmailError(i18n.t("signup.form.emailInvalid"));
			return;
		}
		if (docClean.length !== 14) {
			setDocumentError(i18n.t("signup.form.cnpjIncomplete"));
			return;
		}
		if (!isValidCnpj(docClean)) {
			setDocumentError(i18n.t("signup.form.cnpjInvalid"));
			return;
		}
		setLoading(true);
		setCodeError("");
		try {
			await api.post("/auth/signup/send-code", {
				email: emailTrim,
				document: docClean,
			});
			toast.success(i18n.t("signup.toasts.codeSent"));
			setStep(2);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	};

	const handleVerifyCode = async (e) => {
		e.preventDefault();
		setCodeError("");
		const emailTrim = email.trim().toLowerCase();
		const codeStr = code.replace(/\D/g, "").slice(0, 6);
		if (codeStr.length !== 6) {
			setCodeError(i18n.t("signup.errors.codeInvalidOrExpired"));
			return;
		}
		setLoading(true);
		try {
			const { data } = await api.post("/auth/signup/verify-code", {
				email: emailTrim,
				code: codeStr,
			});
			setVerificationToken(data.verificationToken || "");
			toast.success(i18n.t("signup.toasts.emailValidated"));
			setStep(3);
		} catch (err) {
			toastError(err);
			setCodeError(i18n.t("signup.errors.codeInvalidOrExpired"));
		}
		setLoading(false);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setDocumentError("");
		setPasswordError("");
		if (docClean.length !== 14 || !isValidCnpj(docClean)) {
			setDocumentError(i18n.t("signup.form.cnpjInvalid"));
			return;
		}
		const pwdCheck = validatePassword(password);
		if (!pwdCheck.valid) {
			setPasswordError(i18n.t(pwdCheck.key));
			return;
		}
		const digits = phoneNumber.replace(/\D/g, "");
		const country = COUNTRY_LIST.find((c) => c.code === phoneCountry);
		const fullPhone =
			country && digits.length ? `+${country.ddi}${digits}` : undefined;
		setLoading(true);
		try {
			await api.post("/auth/signup", {
				companyName: companyName || name || "Empresa",
				document: docClean,
				name,
				email: email.trim().toLowerCase(),
				password,
				phone: fullPhone,
				verificationToken,
			});
			toast.success(i18n.t("signup.toasts.success"));
			history.push("/login");
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	};

	const maxPhoneLen =
		phoneCountry === "BR" ? 11 : phoneCountry === "US" ? 10 : 15;

	const renderStep1 = () => (
		<form noValidate onSubmit={handleSendCode} className="space-y-4">
			<ModernAuthField
				id="email"
				label={i18n.t("signup.form.email")}
				type="email"
				name="email"
				placeholder={i18n.t("signup.form.emailPlaceholder")}
				value={email}
				onChange={(e) => {
					setEmail(e.target.value);
					setEmailError("");
				}}
				autoComplete="email"
				autoFocus
				required
				error={emailError}
			/>
			<ModernAuthField
				id="document"
				label={i18n.t("signup.form.cnpj")}
				type="text"
				name="document"
				placeholder={i18n.t("signup.form.cnpjPlaceholder")}
				value={formatCnpj(document)}
				onChange={(e) => {
					setDocument(e.target.value.replace(/\D/g, "").slice(0, 14));
					setDocumentError("");
				}}
				maxLength={18}
				required
				error={documentError}
			/>
			<ModernAuthField
				id="companyName"
				label={i18n.t("signup.form.companyName")}
				type="text"
				name="companyName"
				placeholder={i18n.t("signup.form.companyNamePlaceholder")}
				value={companyName}
				onChange={(e) => setCompanyName(e.target.value)}
				autoComplete="organization"
			/>
			<button type="submit" className={submitButtonClass} disabled={loading}>
				{loading
					? i18n.t("signup.buttons.sendingCode")
					: i18n.t("signup.buttons.sendCode")}
			</button>
		</form>
	);

	const renderStep2 = () => (
		<form noValidate onSubmit={handleVerifyCode} className="space-y-4">
			<ModernAuthField
				id="code"
				label={i18n.t("signup.form.code")}
				type="text"
				name="code"
				placeholder={i18n.t("signup.form.codePlaceholder")}
				value={code}
				onChange={(e) => {
					setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
					setCodeError("");
				}}
				maxLength={6}
				inputMode="numeric"
				autoFocus
				required
				error={codeError}
			/>
			<button type="submit" className={submitButtonClass} disabled={loading}>
				{loading
					? i18n.t("signup.buttons.validatingEmail")
					: i18n.t("signup.buttons.validateEmail")}
			</button>
		</form>
	);

	const renderStep3 = () => (
		<form noValidate onSubmit={handleSubmit} className="space-y-4">
			<ModernAuthField
				id="companyName"
				label={i18n.t("signup.form.companyName")}
				type="text"
				name="companyName"
				placeholder={i18n.t("signup.form.companyNamePlaceholder")}
				value={companyName}
				onChange={(e) => setCompanyName(e.target.value)}
				autoComplete="organization"
				autoFocus
			/>
			<ModernAuthField
				id="name"
				label={i18n.t("signup.form.name")}
				type="text"
				name="name"
				placeholder={i18n.t("signup.form.namePlaceholder")}
				value={name}
				onChange={(e) => setName(e.target.value)}
				autoComplete="name"
				required
			/>
			<div>
				<ModernAuthField
					id="password"
					label={i18n.t("signup.form.password")}
					type={showPassword ? "text" : "password"}
					name="password"
					placeholder={i18n.t("signup.form.passwordPlaceholder")}
					value={password}
					onChange={(e) => {
						setPassword(e.target.value);
						setPasswordError("");
					}}
					autoComplete="new-password"
					required
					error={passwordError}
					hint={!passwordError ? i18n.t("signup.form.passwordHint") : undefined}
					suffix={
						<button
							type="button"
							onClick={() => setShowPassword((prev) => !prev)}
							className="text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-700"
						>
							{showPassword
								? i18n.t("login.buttons.hidePassword")
								: i18n.t("login.buttons.showPassword")}
						</button>
					}
				/>
				{password.length > 0 && (
					<div className="mt-2 flex items-center gap-1">
						{[1, 2, 3].map((i) => (
							<div
								key={i}
								className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
									i <= strength
										? strength === 1
											? "bg-red-400"
											: strength === 2
												? "bg-amber-400"
												: "bg-emerald-500"
										: "bg-zinc-100"
								}`}
							/>
						))}
						<span className="ml-1 text-xs text-zinc-400">
							{strength === 1
								? i18n.t("signup.form.passwordStrengthWeak")
								: strength === 2
									? i18n.t("signup.form.passwordStrengthMedium")
									: i18n.t("signup.form.passwordStrengthStrong")}
						</span>
					</div>
				)}
			</div>
			<div className="flex flex-col gap-2 sm:flex-row sm:items-end">
				<ModernAuthField
					id="phoneCountry"
					label={i18n.t("signup.form.countryCode")}
					className="sm:min-w-[8rem]"
				>
					<select
						id="phoneCountry"
						className={selectClass}
						value={phoneCountry}
						onChange={(e) => setPhoneCountry(e.target.value)}
					>
						{COUNTRY_LIST.map((c) => (
							<option key={c.code} value={c.code}>
								{getFlagEmoji(c.code)} +{c.ddi} {c.name}
							</option>
						))}
					</select>
				</ModernAuthField>
				<ModernAuthField
					id="phone"
					label={i18n.t("signup.form.phone")}
					type="tel"
					name="phone"
					placeholder={i18n.t("signup.form.phonePlaceholder")}
					value={formatPhoneByCountry(phoneCountry, phoneNumber)}
					onChange={(e) => {
						const raw = e.target.value.replace(/\D/g, "").slice(0, maxPhoneLen);
						setPhoneNumber(raw);
					}}
					inputMode="numeric"
					className="flex-1"
				/>
			</div>
			{documentError && (
				<p className="text-xs text-red-500">{documentError}</p>
			)}
			<button type="submit" className={submitButtonClass} disabled={loading}>
				{loading
					? i18n.t("signup.buttons.creating")
					: i18n.t("signup.buttons.submit")}
			</button>
		</form>
	);

	const stepTitles = {
		1: i18n.t("signup.step1Title"),
		2: i18n.t("signup.step2Title"),
		3: i18n.t("signup.step3Title"),
	};

	return (
		<AuthSplitLayout testId="signup-page">
			<div className="mb-8">
				<h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
					{i18n.t("signup.title")}
				</h1>
				<p className="mt-1.5 text-sm text-zinc-500">{stepTitles[step]}</p>
			</div>

			{step === 1 && renderStep1()}
			{step === 2 && renderStep2()}
			{step === 3 && renderStep3()}

			<p className="mt-8 text-center text-sm text-zinc-500">
				{i18n.t("signup.buttons.loginPrefix")}{" "}
				<RouterLink
					to="/login"
					className="font-medium text-zinc-900 underline-offset-2 hover:underline"
				>
					{i18n.t("signup.buttons.loginLink")}
				</RouterLink>
			</p>
		</AuthSplitLayout>
	);
}

export default Signup;
